import { forwardRef, useState, useEffect } from 'react';
import {
	TypographyComponent,
	TypographyVariant,
	TypographySize,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import {
	IconFeedbackSuccessComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import {
	DropDownItem,
	DropDownIcon,
	DropDownText,
	DropDownTextIcon
} from './drop-down-item.styles';

import { SelectOption } from '../../types/config';

interface DropDownItemComponentProps {
	option: SelectOption;
	onSelect: (option: SelectOption) => void;
	isActive: boolean;
	itemIcon?: React.ReactElement;
}

const DropDownItemComponent = forwardRef<
	HTMLButtonElement,
	DropDownItemComponentProps
>(({ option, onSelect, isActive = false, itemIcon }, ref) => {
	const { label, value, selected } = option;

	const [isSelected, setIsSelected] = useState<boolean>(selected);

	useEffect(() => {
		setIsSelected(selected);
	}, [selected]);

	const handleClick = () => {
		if (isSelected) return;
		onSelect(option);
		setIsSelected((prevSelected) => !prevSelected);
	};

	return (
		<DropDownItem
			onClick={(): void => handleClick()}
			selected={isSelected}
			disabled={isSelected}
			type="button"
			data-cy="drop-down-item-component"
			data-item={option.value}
			tabIndex={isActive ? 0 : -1}
			ref={ref}
			role="option">
			{itemIcon ? (
				<DropDownText>
					<DropDownTextIcon>{itemIcon}</DropDownTextIcon>
					<TypographyComponent
						element="span"
						variant={TypographyVariant.Body}
						size={TypographySize.Sml}
						weight={
							isSelected ? TypographyWeight.Medium : TypographyWeight.Regular
						}>
						{label}
					</TypographyComponent>
				</DropDownText>
			) : (
				<TypographyComponent
					element="span"
					variant={TypographyVariant.Body}
					size={TypographySize.Sml}
					weight={
						isSelected ? TypographyWeight.Medium : TypographyWeight.Regular
					}>
					{label}
				</TypographyComponent>
			)}

			<DropDownIcon
				selected={isSelected}
				data-cy="drop-down-item-icon-component">
				{isSelected && <IconFeedbackSuccessComponent size={IconsSize.Sml} />}
			</DropDownIcon>
		</DropDownItem>
	);
});

export default DropDownItemComponent;
